import { Position } from 'src/app/core/types';
import { LoadGroup } from './group';
import { Load } from './load';
import { clearTextMaterialCache } from './load-texture-generator';
import { BufferGeometry, MeshBasicMaterial, MeshLambertMaterial } from 'three';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

export class LoadFactory {
  public static loads = new Map<string, Load>();

  public static get(
    loadGroup: LoadGroup,
    options: LoadOptions,
    loadMeshMaterial?: MeshBasicMaterial[] | MeshLambertMaterial[],
    loadGeometry?: BufferGeometry,
    edges?: LineSegmentsGeometry,
    isShadows?: boolean,
    isPreview?: boolean,
    isPrint?: boolean,
  ) {
    const { position, stacked, covered } = options;
    const key = this.getKey(position, loadGroup.dimensions);
    if (!this.loads.has(key)) {
      return this.create(
        loadGroup,
        options,
        loadMeshMaterial,
        loadGeometry,
        edges,
        isShadows,
        isPreview,
        isPrint,
      );
    }
    const load: Load = this.loads.get(key);
    load.stacking = loadGroup.properties.stacking;
    load.stacked = stacked;
    load.loadType = loadGroup.loadType;
    load.revert = loadGroup.properties.rotated;
    load.dragged = loadGroup.properties.dragged;
    load.maxLoadsOnTop = loadGroup.maxLoadsOnTop;
    load.nameTranslated = loadGroup.nameTranslated;
    load.showLoadName = loadGroup.showLoadName;
    load.rotateOptions = loadGroup.rotateOptions;
    load.covered = covered;
    return load;
  }

  public static create(
    loadGroup: LoadGroup,
    options: LoadOptions,
    loadMeshMaterial?: MeshBasicMaterial[] | MeshLambertMaterial[],
    loadGeometry?: BufferGeometry,
    edges?: LineSegmentsGeometry,
    isShadows?: boolean,
    isPreview?: boolean,
    isPrint?: boolean,
  ): Load {
    const { position, stacked, covered, units } = options;
    return new Load(
      loadGroup.id,
      loadGroup.loadType,
      loadGroup.name,
      loadGroup.dimensions.length,
      loadGroup.dimensions.width,
      loadGroup.dimensions.height,
      loadGroup.dimensions.diameter,
      loadGroup.weight,
      loadGroup.yRotation,
      loadGroup.edgeRotation,
      loadGroup.properties.stacking,
      loadGroup.color,
      loadGroup.createdAt,
      loadGroup.packed,
      position.x,
      position.y,
      position.z,
      loadGroup.properties.rotated,
      loadGroup.properties.dragged,
      stacked,
      loadGroup.maxLoadsOnTop,
      loadGroup.nameTranslated,
      loadGroup.showLoadName,
      covered,
      units,
      loadMeshMaterial,
      loadGeometry,
      edges,
      isShadows,
      undefined,
      loadGroup.centerOfMass,
      loadGroup.rotateOptions,
      isPreview,
      undefined,
      loadGroup.allowOutOfSpace,
      isPrint,
      null,
      loadGroup.pctSupport,
      loadGroup.stackingOptions?.formation,
      loadGroup.stackingOptions?.interlayer === true,
    );
  }

  public static delete(load: Load) {
    const key = this.getKey(load.position, {
      width: load.width,
      height: load.height,
      length: load.length,
    });
    this.loads.delete(key);
  }

  public static getKey(
    position: Position,
    dimensions?: { width?: number; height?: number; length?: number },
  ) {
    const width = dimensions?.width ?? 0;
    const height = dimensions?.height ?? 0;
    const length = dimensions?.length ?? 0;
    return `${position.x}_${position.y}_${position.z}_${width}_${height}_${length}`;
  }

  public static size() {
    return this.loads.size;
  }

  public static set(position: Position, load: Load) {
    return this.loads.set(
      this.getKey(position, { width: load.width, height: load.height, length: load.length }),
      load,
    );
  }

  public static clear() {
    this.loads.clear();
    clearTextMaterialCache();
  }

  public static newPosition(load: Load, previousPosition: Position) {
    this.loads.delete(
      this.getKey(previousPosition, { width: load.width, height: load.height, length: load.length }),
    );
    this.loads.set(
      this.getKey(load.position, { width: load.width, height: load.height, length: load.length }),
      load,
    );
  }
}

interface LoadOptions {
  position: Position;
  stacked: boolean;
  covered: {
    coveredDown: boolean;
    coveredFront: boolean;
    coveredBack: boolean;
    coveredRight: boolean;
    coveredLeft: boolean;
  };
  units: string;
}
